/* eslint-disable no-redeclare */
import { type InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { ReturnForMethod, ModelName, InfiniteQueryMethodForModel, ParamsForMethod } from "../types";
import type { UseTellescopeInfiniteQueryOptions, UseTellescopeInfiniteQueryResult } from "./types";
import { buildTellescopeQueryKey } from "../keys";
import { useTellescopeSession, useTellescopeTanstackConfigContext } from "../context";
import { executeTellescopeApiMethod } from "../utils/execute-tellescope-api-method";
import { useMemo } from "react";
import { addKeyToQueryResult, shouldCrossShareQueryCache } from "../utils";
import { seedIndividualQueryCache } from "../cache/seed-individual-query-cache";
import { TellescopeModelFromName } from "../mappings";

const defaultPaginateRequest = (lastId: any, request: any) => ({
  ...request,
  lastId,
});

const defaultGetNextPageParam = <T extends { id: string }>(lastPage: T[]) => lastPage.at(-1)?.id;

const defaultInitialPageParam: string | undefined = undefined;

/**
 * React hook for paginated/infinite queries from the Tellescope API using TanStack Query.
 *
 * This hook wraps TanStack Query's `useInfiniteQuery` hook with Tellescope-specific functionality,
 * providing type-safe access to paginated API methods with automatic query key generation and caching.
 *
 * Pagination must be explicitly configured using TanStack Query's `getNextPageParam`, `initialPageParam`,
 * and optionally the `paginateRequest` function to merge pagination params into requests.
 *
 * @template Model - The Tellescope model name with paginated query methods (e.g., 'users', 'appointments')
 * @template Method - The paginated query method for the specified model (e.g., 'getSome')
 * @template TError - The error type (defaults to Error)
 * @template TData - The returned data type (defaults to InfiniteData<method's return type>)
 * @template TPageParam - The page parameter type (defaults to { lastId?: string })
 *
 * @param options - Infinite query configuration options
 * @param options.model - The Tellescope model to query (e.g., 'users', 'appointments')
 * @param options.method - The paginated query method to call on the model (e.g., 'getSome')
 * @param options.request - The base request parameters for the API method (without pagination params)
 * @param options.paginateRequest - Optional function to merge pagination params into the request. Receives pageParam and request, returns the modified request with pagination params.
 * @param options.getNextPageParam - Required function to extract pagination params from the last page. Return undefined when no more pages.
 * @param options.initialPageParam - Required initial pagination param for the first page.
 * @param options.session - Optional Tellescope session to use instead of the context session
 * @param options...tanstackOptions - All other TanStack Query infinite query options are supported except `queryKey`, `queryFn`, and `getPreviousPageParam`
 * @returns TanStack Query infinite query result object with pages data, loading state, error state, fetchNextPage utility, and the computed query key
 *
 * @example
 * ```tsx
 * // Basic infinite query with cursor-based pagination
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useTellescopeInfiniteQuery({
 * model: 'users',
 * method: 'getSome',
 * request: { filter: { roles: ['admin'] }, limit: 50 },
 * getNextPageParam: (lastPage) => {
 * const lastId = lastPage[lastPage.length - 1]?.id;
 * return lastId ? { lastId } : undefined;
 * },
 * paginateRequest: (pageParam, request) => ({
 * ...request,
 * lastId: pageParam.lastId,
 * }),
 * initialPageParam: { lastId: "" },
 * });
 *
 * // Access paginated data
 * const allUsers = data?.pages.flat() ?? [];
 *
 * // Load more button
 * <button onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
 * {isFetchingNextPage ? 'Loading...' : hasNextPage ? 'Load More' : 'No more data'}
 * </button>
 *
 * // With additional TanStack Query options
 * const { data } = useTellescopeInfiniteQuery({
 * model: 'calendar_events',
 * method: 'getSome',
 * request: { filter: { status: 'scheduled' } },
 * getNextPageParam: (lastPage) => lastPage.length > 0 ? { lastId: lastPage[lastPage.length - 1].id } : undefined,
 * paginateRequest: (pageParam, request) => ({ ...request, ...pageParam }),
 * initialPageParam: { lastId: undefined },
 * enabled: isReady,
 * staleTime: 5000
 * });
 *
 * // For non-paginated queries, use useTellescopeQuery instead:
 * const { data } = useTellescopeQuery({
 * model: 'users',
 * method: 'getOne',
 * request: { id: 'user123' }
 * });
 * ```
 */
// Overload 1: "getSome" method defaults (pagination options optional)
export function useTellescopeInfiniteQuery<
  Model extends ModelName,
  Method extends Extract<InfiniteQueryMethodForModel<Model>, "getSome">,
  TPageParam = string | undefined,
  TError = Error,
  TData = InfiniteData<ReturnForMethod<Model, Method>>,
>(
  options: Omit<
    UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>,
    "paginateRequest" | "getNextPageParam" | "initialPageParam"
  > & {
    paginateRequest?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["paginateRequest"];
    getNextPageParam?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["getNextPageParam"];
    initialPageParam?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["initialPageParam"];
  }
): UseTellescopeInfiniteQueryResult<Model, Method, TError, TData>;

// Overload 2: General usage (pagination options required)
export function useTellescopeInfiniteQuery<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = InfiniteData<ReturnForMethod<Model, Method>>,
>(
  options: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>
): UseTellescopeInfiniteQueryResult<Model, Method, TError, TData>;

// Implementation
export function useTellescopeInfiniteQuery<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = InfiniteData<ReturnForMethod<Model, Method>>,
>(
  options: Omit<
    UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>,
    "paginateRequest" | "getNextPageParam" | "initialPageParam"
  > & {
    // These must be marked optional here to satisfy Overload 1
    paginateRequest?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["paginateRequest"];
    getNextPageParam?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["getNextPageParam"];
    initialPageParam?: UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>["initialPageParam"];
  }
): UseTellescopeInfiniteQueryResult<Model, Method, TError, TData> {
  const {
    model,
    method,
    request,
    paginateRequest: userPaginateRequest,
    session: providedSession,
    requestKeysToOmitFromQueryKey,
    requestSideEffect,
    responseSideEffect,
    crossQueryCacheSharing,
    getNextPageParam: userGetNextPageParam,
    initialPageParam: userInitialPageParam,
    ...tanstackOptions
  } = options as UseTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData>;

  const session = useTellescopeSession(providedSession);
  const queryClient = useQueryClient();
  const resolvedConfig = useTellescopeTanstackConfigContext({ crossQueryCacheSharing });

  // Apply defaults if method is "getSome"
  const isGetSome = method === "getSome";

  const paginateRequest = userPaginateRequest ?? (isGetSome ? defaultPaginateRequest : undefined);
  const getNextPageParam = userGetNextPageParam ?? (isGetSome ? defaultGetNextPageParam : undefined);
  const initialPageParam = userInitialPageParam ?? (isGetSome ? defaultInitialPageParam : undefined);

  const queryKey = useMemo(
    () =>
      buildTellescopeQueryKey({
        model,
        method,
        request,
        requestKeysToOmitFromQueryKey,
      }),
    [model, method, request, requestKeysToOmitFromQueryKey]
  );

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should be disabled is session is `null`");
      }

      const paginatedRequest: ParamsForMethod<Model, Method> = paginateRequest
        ? // @ts-expect-error - Complex inference on the union of pagination strategies
          paginateRequest(pageParam, request)
        : request;
      requestSideEffect?.(paginatedRequest);

      const res = await executeTellescopeApiMethod({
        session,
        model,
        method,
        request: paginatedRequest,
        parseISOStrings: true,
      });
      responseSideEffect?.(paginatedRequest, res);

      // Seed getOne caches from paginated response items
      if (
        shouldCrossShareQueryCache({ config: resolvedConfig.crossQueryCacheSharing, model, method }) &&
        Array.isArray(res)
      ) {
        const items = res as Array<TellescopeModelFromName<Model>>;
        const firstItem = items[0];
        if (
          items.length > 0 &&
          typeof firstItem === "object" &&
          firstItem !== null &&
          "id" in firstItem &&
          typeof firstItem.id === "string"
        ) {
          seedIndividualQueryCache({ queryClient, model, items });
        }
      }

      return res;
    },
    // We can safely cast these as the defaults ensure they exist for 'getSome',
    // and TS enforces they exist for others via Overload 2.
    initialPageParam: initialPageParam as TPageParam,
    getNextPageParam,
    ...tanstackOptions,
    enabled: session !== null && (tanstackOptions.enabled ?? true),
  });

  return addKeyToQueryResult(queryResult, queryKey);
}
